import { useQueries } from '@tanstack/react-query';
import { usePatientInsurance } from '../../../../../../hooks/queries/usePatientInsurance';
import { usePatientRecord } from '../../../../../../hooks/queries/usePatientRecord';
import { adjudicatorLookupQueryOptions, directoryKeyOf } from '../../../../../../hooks/queries/useAdjudicatorLookup';
import { defaultCoverage, type CoverageDefault, type PlanDirectoryAnswer, type PlansRead } from './vacationCoverage';

/**
 * PRX-130 — the default coverage from the patient's plans: the Insurance
 * tab's plan read (`usePatientInsurance`), the patient record's PMS, and the
 * PRX-128 directory lookup for each active Fillware plan (same cache entries
 * as the plan hover card).
 */
export function useVacationCoverageDefault(pharmacyId: string, patientId: string): CoverageDefault {
  const plansQuery = usePatientInsurance(patientId);
  const record = usePatientRecord(pharmacyId, patientId);
  const databaseType = record.data?.databaseType ?? null;
  const plans = plansQuery.data?.plans ?? [];
  const keys = plans.map((plan) =>
    databaseType === 'fillware' && !plan.inactive ? directoryKeyOf(plan.adjHostId, plan.carrierId) : null,
  );
  const lookups = useQueries({
    queries: keys.map((key) => adjudicatorLookupQueryOptions(pharmacyId, key)),
  });

  if (record.isLoading) return { coverage: null, source: "Reading the patient's pharmacy system", pending: true };
  if (record.isError && !databaseType) {
    return { coverage: null, source: "Couldn't read which pharmacy system this patient is on. Choose the coverage.", pending: false };
  }

  let read: PlansRead;
  if (plansQuery.isLoading) read = { status: 'loading' };
  else if (plansQuery.isError || !plansQuery.data) read = { status: 'error' };
  else {
    read = {
      status: 'ready',
      plans: plans.map((plan, i) => {
        if (!keys[i]) return { plan, directory: null };
        const q = lookups[i];
        const directory: PlanDirectoryAnswer = q.isLoading
          ? { status: 'loading' }
          : q.isError || !q.data
            ? { status: 'error' }
            : { status: 'ready', plans: q.data.plans };
        return { plan, directory };
      }),
    };
  }
  return defaultCoverage(read, databaseType);
}
